import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { checkHDPenny, type HDPennyCheck } from "../../services/ToolsService";
import { toolInputClass } from "./toolUi";
import { getProductImageUrl } from "../../utils/imageUtils";

const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

const BAND_LABEL: Record<NonNullable<HDPennyCheck["dealhawk"]["storeBand"]>, string> = {
  one: "at one store",
  few: "at a few stores",
  many: "at many stores",
  chainwide: "chain-wide (likely being pulled everywhere)",
};

/** Mirrors the API's NormalizeHDPennyQuery so obvious typos never spend a lookup. */
function normalize(raw: string): string | null {
  if (/[^\d\s#-]/.test(raw)) return null;
  const digits = raw.replace(/\D/g, "");
  return [6, 9, 10].includes(digits.length) ? digits : null;
}

export default function HDPennyCheckerTool() {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState<HDPennyCheck | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const sku = normalize(query);
    if (!sku) {
      setError("Enter a store SKU (6 or 10 digits) or Internet # (9 digits).");
      return;
    }
    setError("");
    setLoading(true);
    try {
      setResult(await checkHDPenny(sku));
    } catch (err) {
      const status = (err as { status?: number }).status;
      setResult(null);
      setError(status === 429 ? "You've hit the lookup limit. Try again later, or sign up to see full penny lists for your stores." : "The checker is unavailable right now. Try again in a minute.");
    } finally {
      setLoading(false);
    }
  };

  const item = result?.item;
  return (
    <div>
      <form onSubmit={submit} className="flex gap-2">
        <input
          className={toolInputClass}
          inputMode="numeric"
          placeholder="Store SKU or Internet #"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Home Depot SKU or Internet number"
          maxLength={20}
        />
        <button type="submit" disabled={loading} className="inline-flex flex-shrink-0 items-center gap-2 rounded-xl bg-[#F96302] px-5 py-2.5 font-semibold text-white hover:bg-[#e05600] disabled:opacity-60">
          <Search className="h-4 w-4" /> {loading ? "Checking…" : "Check"}
        </button>
      </form>
      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      {result && !result.found && (
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4" role="status">
          <p className="text-lg font-bold">No penny signals for {result.query}</p>
          <p className="mt-1 text-slate-600">
            DealHawk's crawl isn't flagging it and it isn't on the community penny lists right now. That doesn't rule out a penny at your store — clearance items ending in .03 are still worth scanning.
          </p>
        </div>
      )}

      {result?.found && item && (
        <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4 sm:p-5" role="status">
          <div className="flex gap-4">
            {item.image && <img src={getProductImageUrl(item.image, "400")} alt="" width={80} height={80} className="h-20 w-20 flex-shrink-0 rounded-lg object-contain" />}
            <div className="min-w-0">
              <p className="text-xs text-slate-500">{item.brand}</p>
              <p className="font-bold leading-snug">{item.name || `Item ${result.query}`}</p>
              <p className="mt-1 text-xs text-slate-500">
                {item.sku ? <>SKU {item.sku}</> : <>Internet # {item.itemId}</>}
                {item.regularPrice ? <> · regular {money(item.regularPrice)}</> : null}
              </p>
            </div>
          </div>
          <ul className="mt-4 space-y-2">
            <li className={`rounded-lg p-3 ${result.dealhawk.flagged ? "bg-amber-50 text-amber-900" : "bg-slate-50 text-slate-700"}`}>
              {result.dealhawk.flagged ? (
                <><strong>Flagged by DealHawk</strong> as a possible penny {result.dealhawk.storeBand ? BAND_LABEL[result.dealhawk.storeBand] : ""}.</>
              ) : (
                <>Not flagged in DealHawk's store crawl right now.</>
              )}
            </li>
            <li className={`rounded-lg p-3 ${result.community?.reported ? "bg-emerald-50 text-emerald-900" : "bg-slate-50 text-slate-700"}`}>
              {result.community?.reported ? (
                <>
                  <strong>Reported on community penny lists</strong>
                  {result.community.states?.length ? <> in {result.community.states.join(", ")}</> : null}
                  {result.community.lastReportedAt ? <> · last {result.community.lastReportedAt.slice(0, 10)}</> : null}
                  {result.community.receipt ? <> · receipt posted</> : null}
                </>
              ) : (
                <>Not on the community penny lists we track.</>
              )}
            </li>
          </ul>
          {result.dealhawk.flagged && (
            <div className="mt-4 rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm text-violet-900">
              <strong>Which stores, how many on hand, and the aisle?</strong> Penny Pro shows it for the stores near you.{" "}
              <Link to="/get-started" className="font-semibold underline">Try DealHawk</Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
